import { useReactiveVar } from '@apollo/client';
import { useMemo } from 'react';

import { EntityType } from '../../apis/graphql/types';
import useFetchUserByOrganization from '../../apis/graphql/user/useFetchUserByOrganization';
import { selectedOrganizationIdVar } from '../../constants';
import { USER_ROLES } from './constants';
import { Permissions } from './models';
import { CAMPAIGN_LINE_ITEM_DRAFT, CAMPAIGN_ORDERS } from './permissionsByEntityType';
import { FOUNDING_SOURCE_AND_PAYMENT_METHOD } from './permissionsByEntityType/billing';
import { checkUserRole, checkUserStatus, getEntityTypes, getPermissions } from './utils';

export type UseUserPermissions = {
  isStatusActive: boolean;
  isStatusInvited: boolean;
  isStatusUnavailable: boolean;
  loading: boolean;
  defaultIsModificationAllowed: boolean;
  entityTypes: EntityType[];
  billing: Permissions;
  campaignsOrders: Permissions;
  campaignLineItemDraft: Permissions;
};

const useUserPermissions = (): UseUserPermissions => {
  const selectedOrganizationId = useReactiveVar(selectedOrganizationIdVar);
  const { data, loading } = useFetchUserByOrganization(selectedOrganizationId);

  const statuses = useMemo(() => checkUserStatus(data?.status), [data?.status]);
  const roles = useMemo(() => checkUserRole(data?.roleList), [data?.roleList]);
  const entityTypes = useMemo(() => getEntityTypes(data?.roleList), [data?.roleList]);

  const defaultIsModificationAllowed = useMemo(
    () => !roles[USER_ROLES.creativeManager] && !roles[USER_ROLES.member] && !roles[USER_ROLES.adAccountAnalyst],
    [roles]
  );

  const billing: Permissions = useMemo(
    () => getPermissions(FOUNDING_SOURCE_AND_PAYMENT_METHOD, roles, entityTypes),
    [entityTypes, roles]
  );

  const campaignsOrders: Permissions = useMemo(
    () => getPermissions(CAMPAIGN_ORDERS, roles, entityTypes),
    [entityTypes, roles]
  );

  const campaignLineItemDraft: Permissions = useMemo(
    () => getPermissions(CAMPAIGN_LINE_ITEM_DRAFT, roles, entityTypes),
    [entityTypes, roles]
  );

  return {
    isStatusActive: statuses.isActive,
    isStatusInvited: statuses.isInvited,
    isStatusUnavailable: statuses.isUnavailable,

    entityTypes,

    loading,
    defaultIsModificationAllowed,
    billing,
    campaignsOrders,
    campaignLineItemDraft,
  };
};

export default useUserPermissions;
